import React, { useEffect, useState } from "react";
import { errorMessages, settings } from "app";
import queryString from "query-string";
import { useLocation } from "react-router-dom";

import { useNotify, useRedirect } from "react-admin";
import { applySessionAndLoadUser } from "./authProvider";
import {
  authCallbackPath,
  getAccessToken,
  redirectAfterLogin,
  selectProfilePath,
  setProfileSelectionPending,
  setTokens,
} from "./securityUtils";
import { CircularProgress, Card, CardContent, CardActions, Button, Typography } from "@mui/material";

const LoginCallback = () => {
  const location = useLocation();
  const redirect = useRedirect();
  const notify = useNotify();

  const { code } = queryString.parse(location.search);
  const [showLoader, setShowLoader] = useState(true);

  useEffect(() => {
    // При натискане на "Назад" браузърът връща callback-а от bfcache —
    // замразената страница със spinner-а — без да преизпълни този ефект.
    // pageshow се задейства и при bfcache възстановяване, така че оттам
    // излизаме към началото, ако вече сме логнати (иначе loader-ът върти
    // вечно с вече използван code).
    const handlePageShow = () => {
      if (getAccessToken()) {
        window.location.replace(`${settings.basename || ""}/`);
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  useEffect(() => {
    // Ако вече сме логнати (напр. пълен reload на callback-а, а не bfcache),
    // не разменяме отново кода — връщаме към началото.
    if (getAccessToken()) {
      window.location.replace(`${settings.basename || ""}/`);
      return;
    }

    const fetchData = async () => {
      if (code) {
        fetch(
          `${settings.authorizationApi}/${settings.loginEndpoint || "login"}?partyname=google&redirecturl=${
            window.location.origin
          }/${authCallbackPath}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              code,
              clientId: settings.authClientId,
              partyName: "google",
              redirectUrl: window.location.origin + `/${authCallbackPath}`,
            }),
          },
        )
          .then(async response => {
            if (response.ok && response.headers.get("x-session-id")) {
              // 200 with { requiresProfileSelection, profiles }.
              const payload = await response.json().catch(() => ({}));

              if (payload?.requiresProfileSelection) {
                // The session has no roles yet - the employee must pick a node/position profile first.
                setTokens(response.headers.get("x-session-id"));
                setProfileSelectionPending();
                window.location.href = `/${selectProfilePath}`;
                return;
              }

              // Save the access token and redirect to the admin dashboard
              await applySessionAndLoadUser(response.headers.get("x-session-id"));
              redirectAfterLogin();
            } else {
              // Handle error
              try {
                const payload = await response.json();
                if (payload?.errorCode && errorMessages[payload.errorCode]) {
                  notify(errorMessages[payload.errorCode], { type: "error" });
                } else {
                  notify(errorMessages.default, { type: "error" });
                }
              } catch {
                notify(errorMessages.default, { type: "error" });
              }
              console.error("Failed to obtain access token");
              setShowLoader(false);
              return;
            }
          })
          .catch(error => {
            console.error("Error during token exchange", error);
          });
      }
    };

    fetchData();
  }, [code, redirect, notify]);

  if (!showLoader) {
    return (
      <div style={styles.error}>
        <Card sx={{ maxWidth: "30rem", py: 2, px: 6, mt: 30 }}>
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ fontWeight: "bold" }}
            >
              Входът не беше завършен
            </Typography>
            <Typography
              variant="body2"
              color="error"
            >
              Опитайте отново от страницата за вход.
            </Typography>
          </CardContent>
          <CardActions sx={{ justifyContent: "center" }}>
            <Button
              variant="contained"
              color="error"
              onClick={() => (window.location.href = "/login")}
            >
              Вход
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => window.location.reload()}
            >
              Опитай отново
            </Button>
          </CardActions>
        </Card>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <CircularProgress
        size={200}
        loading={true}
      />
      <div style={styles.text}>Logging in...</div>
    </div>
  );
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    textAlign: "center",
  },
  error: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    height: "100vh",
    textAlign: "center",
    backgroundSize: "cover",
    backgroundImage: "radial-gradient(circle at 50% 14em, #313264 0%, #00023b 60%, #00023b 100%)",
  },
  text: {
    marginTop: "20px",
    fontSize: "24px",
  },
};
export default LoginCallback;
